import React from 'react';

import { QueryOptions } from '@apollo/client';
import {
  Download as DownloadIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Box, Button, Collapse, Grid, IconButton, Typography, useTheme } from '@mui/material';

import { DateFormatPatterns, DEFAULT_VALUE_IF_NIL } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatDate, getDateTimeInTimezone, parseNullableValue } from '@clearverify/utils';

import {
  FileResponseInfoFragment,
  GeneratedReportMetaInfoFragment,
  PiiDataAccessActionType,
  VerificationRequestListItemFragment,
} from 'src/api';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { GeneratedReportsPdfRenderer } from 'src/components/generatedReport';
import { GeneratedReportPreviewCardAction } from 'src/components/generatedReport/GeneratedReportPreviewCard';
import { ModalId } from 'src/constants/modals';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import { useScreenInfo } from 'src/hooks/common';
import { useEmployeeInfoForVerificationRequest } from 'src/hooks/employee';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { usePermissiblePurpose } from 'src/hooks/permissiblePurposes';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import { useVerifierByVerificationRequestId } from 'src/hooks/verifier';
import { SxProp } from 'src/types/mui';

type ViewVerificationRequestReportModalFormProps = {
  inboxItemId: Nullable<string>;
  onCloseModal: () => void;
  handleDownloadAllReports: () => void;
  verificationRequest: Nullable<VerificationRequestListItemFragment>;
  refetchQueriesOnAction: QueryOptions[];
};

export const ViewVerificationRequestReportModalForm: React.FC<
  ViewVerificationRequestReportModalFormProps
> = ({
  inboxItemId,
  onCloseModal,
  verificationRequest,
  handleDownloadAllReports,
  refetchQueriesOnAction,
}) => {
  const theme = useTheme();

  const { openModal } = useModal();
  const { isMobile } = useScreenInfo();

  const { timezone } = useCurrentUser();
  const { hasEmployerRoles, isEmployee, hasPeoRoles, isPeoPayrollProviderUser } =
    useCurrentUserPermissions();

  const [expanded, setExpanded] = React.useState(false);

  const [pdfPreview, setPdfPreview] = React.useState<FileResponseInfoFragment | null>(null);

  const { verifier } = useVerifierByVerificationRequestId(verificationRequest?.id);
  const { permissiblePurpose } = usePermissiblePurpose({
    id: verificationRequest?.permissiblePurposeId,
  });
  const { generatedReportsMeta, downloadGeneratedReportFiles } = useGeneratedReports({
    inboxItemId,
  });
  const { employeeInfoForVerificationRequest } = useEmployeeInfoForVerificationRequest(
    verificationRequest?.employeeId,
  );

  const handlePreview = React.useCallback(
    async (report: GeneratedReportMetaInfoFragment) => {
      const [previewFile] = await downloadGeneratedReportFiles(
        report.file.id,
        PiiDataAccessActionType.Viewed,
        { refetchQueries: refetchQueriesOnAction },
      );
      if (previewFile) {
        setPdfPreview(previewFile);
      }
    },
    [downloadGeneratedReportFiles, refetchQueriesOnAction],
  );

  const handleResetPreview = React.useCallback(() => {
    setPdfPreview(null);
  }, []);

  const handleDownload = React.useCallback(
    async (reportFileId: string) => {
      await downloadGeneratedReportFiles(reportFileId, PiiDataAccessActionType.Downloaded, {
        refetchQueries: refetchQueriesOnAction,
      });
    },
    [downloadGeneratedReportFiles, refetchQueriesOnAction],
  );

  const handleDisputeClick = React.useCallback(() => {
    const generatedReport = generatedReportsMeta.find(
      generatedReport => generatedReport.file.id === pdfPreview?.id,
    );
    if (!generatedReport?.employerId) return;

    openModal(ModalId.CreateDataDisputeModal, {
      cb: onCloseModal,
      employerId: generatedReport.employerId,
    });
  }, [generatedReportsMeta, onCloseModal, openModal, pdfPreview?.id]);

  const handleCardDisputeClick = React.useCallback(
    (generatedReport: Nullable<GeneratedReportMetaInfoFragment>) => {
      openModal(ModalId.CreateDataDisputeModal, {
        cb: onCloseModal,
        employerId: generatedReport?.employerId,
      });
    },
    [onCloseModal, openModal],
  );

  const renderActionButtons = React.useCallback(() => {
    const shouldDisplayDisputeButton = isEmployee && pdfPreview;
    if (isMobile) {
      return (
        <Grid size={12}>
          <Box display="flex" mt={1} gap={4} justifyContent="center">
            {!shouldDisplayDisputeButton && (
              <Button
                variant="contained"
                color="error"
                onClick={handleDisputeClick}
                sx={{ paddingX: 3 }}
              >
                Dispute Data
              </Button>
            )}
            <Button variant="outlined" color="inherit" onClick={handleDownloadAllReports}>
              Download
            </Button>
          </Box>
        </Grid>
      );
    }

    return (
      <Grid size={12}>
        <Box display="flex" mt={2} gap={5} justifyContent="center">
          <Button variant="outlined" color="infoButton" onClick={onCloseModal}>
            Close
          </Button>
          {shouldDisplayDisputeButton && (
            <Button
              variant="contained"
              color="error"
              onClick={handleDisputeClick}
              sx={{ paddingX: 5 }}
            >
              Dispute Data
            </Button>
          )}
        </Box>
      </Grid>
    );
  }, [
    handleDisputeClick,
    handleDownloadAllReports,
    isMobile,
    isEmployee,
    pdfPreview,
    onCloseModal,
  ]);

  const cardActions = React.useMemo<GeneratedReportPreviewCardAction[]>(
    () => [
      {
        name: 'Download',
        icon: <DownloadIcon />,
        onAction: generatedReportFile => {
          if (!generatedReportFile) return;
          downloadGeneratedReportFiles(
            generatedReportFile.file.id,
            PiiDataAccessActionType.Downloaded,
          );
        },
      },
      ...(isEmployee
        ? [
            {
              name: 'Dispute Data',
              icon: <ErrorIcon />,
              onAction: handleCardDisputeClick,
              color: theme.palette.error.main,
            },
          ]
        : []),
    ],
    [downloadGeneratedReportFiles, handleCardDisputeClick, isEmployee, theme.palette.error.main],
  );

  return (
    <Grid container spacing={2}>
      {!hasEmployerRoles && !hasPeoRoles && !isPeoPayrollProviderUser && (
        <React.Fragment>
          <Grid size={6.5}>
            <Typography color="GrayText" variant="body1" sx={textStyles}>
              Verifier Company Name:
            </Typography>
          </Grid>
          <Grid size={5.5}>
            <Typography color="black" variant="body1">
              <DemoPrivacyText>{parseNullableValue(verifier?.displayName)}</DemoPrivacyText>
            </Typography>
          </Grid>
          <Grid size={6.5}>
            <Typography color="GrayText" variant="body1" sx={textStyles}>
              Verifier Contact Name:
            </Typography>
          </Grid>
          <Grid size={5.5}>
            <Typography color="black" variant="body1">
              <DemoPrivacyText>{parseNullableValue(verifier?.contactName)}</DemoPrivacyText>
            </Typography>
          </Grid>
        </React.Fragment>
      )}

      <Grid size={6.5}>
        <Typography color="GrayText" variant="body1" sx={textStyles}>
          Employee Name:
        </Typography>
      </Grid>
      <Grid size={5.5}>
        <Typography color="black" variant="body1">
          <DemoPrivacyText>
            {parseNullableValue(employeeInfoForVerificationRequest?.fullName)}
          </DemoPrivacyText>
        </Typography>
      </Grid>

      <Grid size={6.5}>
        <Typography color="GrayText" variant="body1" sx={textStyles}>
          Request created at:
        </Typography>
      </Grid>
      <Grid size={5.5}>
        <Typography color="black" variant="body1">
          {formatDate(
            getDateTimeInTimezone(verificationRequest?.createdAt, timezone)?.toISO(),
            DateFormatPatterns.meridiemTimePaddedWithDate,
          )}
        </Typography>
      </Grid>

      <Grid size={6.5}>
        <Typography color="GrayText" variant="body1" sx={textStyles}>
          Report Type:
        </Typography>
      </Grid>
      <Grid size={5.5}>
        <Typography color="black" variant="body1" flexWrap="wrap">
          {verificationRequest?.type
            ? VERIFICATION_REQUEST_TYPE_LABEL[verificationRequest.type]
            : DEFAULT_VALUE_IF_NIL}
        </Typography>
      </Grid>

      {!hasEmployerRoles && !isEmployee && !hasPeoRoles && !isPeoPayrollProviderUser && (
        <React.Fragment>
          <Grid size={12}>
            <Box>
              <Typography color="GrayText" variant="body1" component="span" sx={textStyles}>
                Permissible Purposes
              </Typography>
              <IconButton
                sx={expandStyles(expanded)}
                onClick={() => setExpanded(!expanded)}
                size="small"
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Typography color="black" variant="body1" ml={1}>
                {parseNullableValue(permissiblePurpose?.permissiblePurposeReference.label)}
              </Typography>
            </Collapse>
          </Grid>
        </React.Fragment>
      )}

      {isMobile && renderActionButtons()}
      {generatedReportsMeta.length ? (
        <Grid mt={isMobile ? 1 : 2} size={12}>
          <Box padding={3} sx={reportBoxStyles}>
            <GeneratedReportsPdfRenderer
              pdfPreview={pdfPreview}
              onPreview={handlePreview}
              onResetPreview={handleResetPreview}
              onDownload={handleDownload}
              generatedReportsMeta={generatedReportsMeta}
              cardActions={cardActions}
              isSingleReport={hasEmployerRoles}
            />
          </Box>
        </Grid>
      ) : (
        <Grid mt={2} size={12}>
          <Typography variant="body1">The report is generating. Please try again later.</Typography>
        </Grid>
      )}
      {!isMobile && renderActionButtons()}
    </Grid>
  );
};

const expandStyles =
  (expand: boolean): SxProp =>
  theme => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  });

const textStyles: SxProp = theme => ({
  opacity: 1,
  [theme.breakpoints.down('sm')]: {
    opacity: 0.6,
  },
});

const reportBoxStyles: SxProp = theme => ({
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: 2,
});
